// INTADMIN.B2 (#2111) — the caller's platform sub-role, for display gating.
//
// ⚠️ NON-AUTHORITATIVE, exactly like useInternalAdminVisibility. This decides
// whether to ENABLE a control. It is not an authorization decision. The real
// gate is requirePlatformAdmin('SUPER_ADMIN') on
// PUT /api/internal/pricing/:planKey and POST /api/internal/pricing/rate/refresh
// (apps/api/src/routes/internal-pricing.router.ts), which returns 403
// regardless of what this hook says.
//
// The point of it is UX: #2110's shell admits ANY platform admin, so without
// this a SUPPORT_ADMIN opens the pricing panel, edits every field, clicks Save,
// and only then learns the write is forbidden — having lost nothing but their
// time and their confidence in the tool.
//
// ── Why a separate hook rather than widening the existing one ──────────────
//
// useInternalAdminVisibility returns a boolean and its header states plainly
// that it is display-only. Widening its return type to carry a ROLE would make
// it look like an authorization source and invite exactly that misuse. Two
// small hooks with narrow contracts beat one that answers two questions.
//
// The cost is one extra GET /api/internal/session per session, cached the same
// way (sessionStorage, keyed by userId) — not one per page load.
//
// ── Why the role is trustworthy for display ───────────────────────────────
//
// GET /api/internal/session echoes req.user.platform_role, which
// requirePlatformAdmin has just OVERWRITTEN with the value it looked up from
// the platform_admins table (requirePlatformAdmin.ts:117). So this is the DB's
// answer, not the unverified JWT claim — which matters, because that claim
// carries a documented revocation gap of up to 7 days.

import { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { decodeJwtPayload } from '../lib/decodeJwtPayload';

export type PlatformRole = 'SUPER_ADMIN' | 'SUPPORT_ADMIN' | 'BILLING_ADMIN';

interface SessionResponse {
  isPlatformAdmin?: boolean;
  userId?: string;
  platformRole?: PlatformRole | null;
}

export interface InternalAdminRole {
  /** null while the probe is in flight, or if it could not be determined. */
  role: PlatformRole | null;
  /** The caller's own user id, for rendering "You" in updated-by columns. */
  userId: string | null;
  loading: boolean;
  /**
   * May this caller perform pricing mutations? SUPER_ADMIN only — see
   * internal-pricing.router.ts. Defaults to FALSE while loading and on any
   * failure: a control wrongly disabled is a nuisance, one wrongly enabled
   * teaches the user that Save is broken.
   */
  canWrite: boolean;
}

function cacheKey(userId: string): string {
  return `blueprint_internal_admin_role:${userId}`;
}

function readCache(userId: string): PlatformRole | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(userId));
    return raw === null ? null : (raw as PlatformRole);
  } catch {
    // Private mode / storage disabled — degrade to probing every mount.
    return null;
  }
}

// Only ever called with a resolved role. An unknown role is deliberately NOT
// cached: a transient 401/5xx would otherwise pin the caller to "cannot write"
// for the tab's lifetime, since the cache key is the userId and the effect's
// readCache short-circuit would never re-probe. Costs one extra request per
// mount for a non-admin, who cannot reach this page anyway.
function writeCache(userId: string, role: PlatformRole): void {
  try {
    sessionStorage.setItem(cacheKey(userId), role);
  } catch {
    /* no-op — see readCache */
  }
}

export function useInternalAdminRole(): InternalAdminRole {
  const token = localStorage.getItem('blueprint_token');
  const claims = token ? decodeJwtPayload<{ userId?: string }>(token) : null;
  const userId = claims?.userId ?? null;

  const cached = userId !== null ? readCache(userId) : null;

  // State holds only the async probe result, tagged with the userId it belongs
  // to so a previous user's role cannot leak to the next user in the same tab.
  const [probe, setProbe] = useState<{ userId: string; role: PlatformRole | null } | null>(null);

  useEffect(() => {
    if (!token || !userId) return;
    if (readCache(userId) !== null) return;

    let cancelled = false;
    apiFetch<SessionResponse>('/api/internal/session')
      .then(res => {
        if (cancelled) return;
        const role = res?.platformRole ?? null;
        // Only a definitive answer is cached. Caching a null would pin the
        // caller to "cannot write" for the tab's lifetime after one transient
        // failure — the cache key is the userId, so re-authenticating in the
        // same tab would hit the same key and never re-probe.
        if (role !== null) writeCache(userId, role);
        setProbe({ userId, role });
      })
      .catch(() => {
        if (cancelled) return;
        // A 401/403/5xx here is not an answer about which role this user holds.
        // Leave the cache empty and report "unknown", which reads as no-write.
        setProbe({ userId, role: null });
      });

    return () => { cancelled = true; };
  }, [token, userId]);

  const probed = probe !== null && probe.userId === userId ? probe.role : null;
  const role = cached ?? probed;
  const loading = role === null && probe === null && token !== null && userId !== null;

  return {
    role,
    userId,
    loading,
    canWrite: role === 'SUPER_ADMIN',
  };
}
